import React, { useMemo } from 'react';
import CustomSelect from '../../ui/CustomSelect';
import FormLabel from './FormLabel';
import { Switch } from '../../ui/switch';
import {
  formatIdNumber,
  formatNPWP,
  getIdNumberConfig,
  validateIdNumber,
} from './utils/idNumberUtils';
import { DEBTOR_TYPES, ID_TYPES, GENDER_OPTIONS, MARITAL_STATUS_OPTIONS } from './utils/constants';

interface IdentityTabProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  handleFileUpload?: (file: File, field: string) => Promise<string>;
  errors?: Record<string, string>;
}

export default function IdentityTab({
  formData,
  handleChange,
  handleFileUpload,
  errors = {},
}: IdentityTabProps) {
  const idConfig = useMemo(() => getIdNumberConfig(formData.id_type || 'ktp'), [formData.id_type]);

  const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Format berdasarkan jenis identitas
    if (formData.id_type === 'npwp') {
      value = formatNPWP(value);
    } else {
      value = formatIdNumber(value, formData.id_type || 'ktp');
    }

    // Batasi panjang sesuai konfigurasi
    if (idConfig.maxLength && value.length > idConfig.maxLength) {
      value = value.slice(0, idConfig.maxLength);
    }

    handleChange('id_number', value);
  };

  const idNumberError = useMemo(() => {
    if (errors.id_number) return errors.id_number;
    if (formData.id_number) {
      return validateIdNumber(formData.id_number, formData.id_type || 'ktp');
    }
    return '';
  }, [formData.id_number, formData.id_type, errors.id_number]);

  return (
    <div className="space-y-6">
      {/* Top Section: CIF, Tipe Debitur, Toggle and Profile */}
      <div className="space-y-4">
        {/* CIF and Tipe Debitur Row */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FormLabel>CIF (Customer Information File)</FormLabel>
            <input
              type="text"
              value={formData.cif || ''}
              readOnly
              className="w-full h-10 px-3 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              placeholder="Auto-generated"
            />
          </div>
          <div>
            <FormLabel required>Tipe Debitur</FormLabel>
            <div className="h-10">
              <CustomSelect
                value={formData.debtor_type || 'individual'}
                onChange={v => handleChange('debtor_type', v)}
                placeholder="Pilih tipe"
                width="w-full"
                options={DEBTOR_TYPES}
              />
            </div>
          </div>
        </div>

        <div>
          <FormLabel>Wirausaha?</FormLabel>
          <div className="flex items-center space-x-3 mt-2">
            <Switch
              checked={formData.is_entrepreneur || false}
              onCheckedChange={checked => handleChange('is_entrepreneur', checked)}
            />
            <span className="text-sm text-gray-700">
              {formData.is_entrepreneur ? 'Ya' : 'Tidak'}
            </span>
          </div>
        </div>

        <div>
          <FormLabel>Profil Singkat Debitur</FormLabel>
          <textarea
            value={formData.debtor_profile || ''}
            onChange={e => handleChange('debtor_profile', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            placeholder="Jelaskan profil singkat debitur..."
            rows={3}
          />
        </div>
      </div>

      {/* Form Fields: Two Columns - Sejajar per baris */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Baris 1: Nama Lengkap - Jenis Identitas */}
        <div>
          <FormLabel required>Nama Lengkap</FormLabel>
          <input
            type="text"
            value={formData.full_name || ''}
            onChange={e => handleChange('full_name', e.target.value)}
            className={`w-full h-10 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors ${
              errors.full_name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Nama lengkap sesuai identitas"
            required
          />
          {errors.full_name && <p className="text-sm text-red-600 mt-1">{errors.full_name}</p>}
        </div>
        <div>
          <FormLabel required>Jenis Identitas</FormLabel>
          <div className="h-10">
            <CustomSelect
              value={formData.id_type || 'ktp'}
              onChange={v => handleChange('id_type', v)}
              placeholder="Pilih jenis"
              width="w-full"
              options={ID_TYPES}
            />
          </div>
        </div>

        {/* Baris 2: Nomor Identitas - Tempat Lahir */}
        <div>
          <FormLabel required>Nomor Identitas</FormLabel>
          <input
            type="text"
            value={formData.id_number || ''}
            onChange={handleIdNumberChange}
            className={`w-full h-10 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors ${
              idNumberError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={idConfig.placeholder}
            maxLength={idConfig.maxLength}
            pattern={idConfig.pattern || undefined}
            required
          />
          {idNumberError && <p className="text-sm text-red-600 mt-1">{idNumberError}</p>}
          {!idNumberError && formData.id_type && idConfig.formatHint && (
            <p className="text-xs text-gray-500 mt-1">{idConfig.formatHint}</p>
          )}
        </div>
        <div>
          <FormLabel>Tempat Lahir</FormLabel>
          <input
            type="text"
            value={formData.birth_place || ''}
            onChange={e => handleChange('birth_place', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Kota kelahiran"
          />
        </div>

        {/* Baris 3: Tanggal Lahir - Jenis Kelamin */}
        <div>
          <FormLabel>Tanggal Lahir</FormLabel>
          <input
            type="date"
            value={formData.birth_date || ''}
            onChange={e => handleChange('birth_date', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="mm/dd/yyyy"
          />
        </div>
        <div>
          <FormLabel>Jenis Kelamin</FormLabel>
          <div className="h-10">
            <CustomSelect
              value={formData.gender || ''}
              onChange={v => handleChange('gender', v)}
              placeholder="Pilih"
              width="w-full"
              options={GENDER_OPTIONS}
            />
          </div>
        </div>

        {/* Baris 4: Status Pernikahan - Pendidikan Terakhir */}
        <div>
          <FormLabel>Status Pernikahan</FormLabel>
          <div className="h-10">
            <CustomSelect
              value={formData.marital_status || ''}
              onChange={v => handleChange('marital_status', v)}
              placeholder="Pilih status"
              width="w-full"
              options={MARITAL_STATUS_OPTIONS}
            />
          </div>
        </div>
        <div>
          <FormLabel>Pendidikan Terakhir</FormLabel>
          <input
            type="text"
            value={formData.education || ''}
            onChange={e => handleChange('education', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Contoh: S1, SMA, dll"
          />
        </div>

        {/* Baris 5: Upload Foto - No. Telepon / WhatsApp */}
        <div>
          <FormLabel>Upload Foto</FormLabel>
          <div className="h-10 flex items-center">
            <input
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file && handleFileUpload) {
                  handleFileUpload(file, 'photo_url');
                }
              }}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
            />
          </div>
        </div>
        <div>
          <FormLabel>No. Telepon / WhatsApp</FormLabel>
          <input
            type="text"
            value={formData.phone || ''}
            onChange={e => handleChange('phone', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="08xxxxxxxxxx"
          />
        </div>

        {/* Baris 6: Email - Nama Gadis Ibu Kandung */}
        <div>
          <FormLabel>Email</FormLabel>
          <input
            type="email"
            value={formData.email || ''}
            onChange={e => handleChange('email', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="email@domain.com"
          />
        </div>
        <div>
          <FormLabel>Nama Gadis Ibu Kandung</FormLabel>
          <input
            type="text"
            value={formData.mother_maiden_name || ''}
            onChange={e => handleChange('mother_maiden_name', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Nama ibu kandung"
          />
        </div>

        {/* Baris 7: Status - (kosong) */}
        <div>
          <FormLabel>Status</FormLabel>
          <div className="h-10">
            <CustomSelect
              value={formData.status || 'active'}
              onChange={v => handleChange('status', v)}
              placeholder="Pilih status"
              width="w-full"
              options={[
                { value: 'active', label: 'Aktif' },
                { value: 'inactive', label: 'Tidak Aktif' },
                { value: 'blocked', label: 'Diblokir' },
              ]}
            />
          </div>
        </div>
        <div>{/* Field kosong untuk balance */}</div>
      </div>
    </div>
  );
}
